import { useFormik } from 'formik'
import * as Yup from 'yup'
import { FormattedMessage, useIntl } from 'react-intl'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import _ from 'lodash'
import FormikTextField from '../../../../shared/components/formik/FormikTextField'
import FormikAutocompleteField from '../../../../shared/components/formik/FormikAutocompleteField'
import { useRegistries } from '../../../../contexts/RegistriesContext'

const PASSWORD_PLACEHOLDER = '************'

const RegistryForm = ({ registryCredential, createRegistryCredential, updateRegistryCredential, onSuccess, onCancel }) => {
  const intl = useIntl()
  const isEditMode = Boolean(registryCredential)
  const { registries } = useRegistries()

  const initialRegistryUrl = isEditMode
    ? _.get(_.find(registries, { id: registryCredential.registryId }), 'url', '')
    : ''

  const validationSchema = Yup.object({
    registryUrl: Yup.string().required(intl.formatMessage({ id: 'validation.required' })),
    username: Yup.string().required(intl.formatMessage({ id: 'validation.required' })),
    password: isEditMode
      ? Yup.string()
      : Yup.string().required(intl.formatMessage({ id: 'validation.required' }))
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      registryUrl: initialRegistryUrl,
      username: registryCredential?.username || '',
      password: isEditMode ? PASSWORD_PLACEHOLDER : ''
    },
    validationSchema,
    onSubmit: (values, { setSubmitting, setStatus }) => {
      const data = {
        registryCredential: {
          registryUrl: values.registryUrl,
          username: values.username,
        }
      }

      if (values.password && values.password !== PASSWORD_PLACEHOLDER) {
        data.registryCredential.password = values.password
      }

      const apiCall = isEditMode
        ? updateRegistryCredential(registryCredential.id, data)
        : createRegistryCredential(data)

      apiCall
        .then((response) => {
          onSuccess(response)
        })
        .catch((err) => {
          setStatus(err.message || 'Error saving registry credential')
        })
        .finally(() => {
          setSubmitting(false)
        })
    }
  })

  const registryOptions = _.map(registries, (r) => ({
    value: r.url,
    label: `${r.name} (${r.url})`
  }))

  const handlePasswordFocus = () => {
    if (formik.values.password === PASSWORD_PLACEHOLDER) {
      formik.setFieldValue('password', '')
    }
  }

  return (
    <Box component="form" onSubmit={formik.handleSubmit}>
      <FormikAutocompleteField
        formik={formik}
        name="registryUrl"
        label={intl.formatMessage({ id: 'registries.form.registryLabel' })}
        placeholder={intl.formatMessage({ id: 'registries.form.registryPlaceholder' })}
        options={registryOptions}
        freeSolo
      />

      <Box sx={{ mt: 2 }}>
        <FormikTextField
          formik={formik}
          name="username"
          label={<FormattedMessage id="registries.form.usernameLabel" />}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <FormikTextField
          formik={formik}
          name="password"
          type="password"
          label={<FormattedMessage id="registries.form.passwordLabel" />}
          onFocus={handlePasswordFocus}
        />
      </Box>

      {formik.status && (
        <Typography color="error" sx={{ mt: 2 }}>
          {formik.status}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={formik.isSubmitting}
        >
          <FormattedMessage id="app.actions.cancel.label" />
        </Button>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          disabled={formik.isSubmitting}
        >
          <FormattedMessage id="registries.form.submitButton" />
        </Button>
      </Box>
    </Box>
  )
}

export default RegistryForm
